// ✅ Mobile number regex
export const BD_MOBILE_REGEX = /^(13|14|15|16|17|18|19)[0-9]{8}$/;

// ✅ Email regex
export const MAIL_REGEX =
  /^(?![-_.])[a-zA-Z0-9]+(?:[._-][a-zA-Z0-9]+)*@([a-zA-Z0-9]+(-[a-zA-Z0-9]+)*\.)+[a-zA-Z]{2,10}$/;

// ✅ Mobile validator with breakdown messages
export const validateMobile = async (_: any, value: string) => {
  if (!value) {
    return Promise.resolve();
  }

  // Step 1: only allow digits and optional + at start
  const digitsOnlyRegex = /^\+?\d+$/;
  if (!digitsOnlyRegex.test(value)) {
    return Promise.reject(new Error("phone number must contain only numbers"));
  }

  // Step 2: validate Bangladeshi phone number format
  const phoneRegex = /^(\+88)?0?1[3-9]\d{8}$/;
  if (!phoneRegex.test(value)) {
    return Promise.reject(new Error("Please enter a valid phone number"));
  }

  return Promise.resolve();
};

const isValidTLD = (email: string) => {
  // Extract TLD from email
  const tld = email.split(".").pop()?.toLowerCase();
  // You can define allowed TLDs or use a simple check length >= 2
  return !!tld && tld.length >= 2 && tld.length <= 10;
};

// ✅ Email validator with breakdown messages
export const validateEmail = async (_: any, value: string) => {
  if (!value) {
    return Promise.resolve();
  }

  if (!MAIL_REGEX.test(value)) {
    return Promise.reject(new Error("Please enter a valid email"));
  }

  if (!isValidTLD(value)) {
    return Promise.reject(new Error("Please enter a valid TLD"));
  }

  return Promise.resolve();
};

// ✅ Bangladeshi mobile banking number validator (supports 88 prefix)
export const validateMobileBankAccount = async (_: any, value: string) => {
  if (!value) {
    return Promise.resolve();
  }

  // Step 1: only digits
  const digitsOnlyRegex = /^\d+$/;
  if (!digitsOnlyRegex.test(value)) {
    return Promise.reject(
      new Error("Mobile banking number must contain only digits"),
    );
  }

  // Step 2: allow either 11 digits or 13 digits with '88' prefix
  let normalized = value;
  if (value.startsWith("88")) {
    if (value.length !== 13) {
      return Promise.reject(
        new Error("Mobile banking number with country code must be 13 digits"),
      );
    }
    normalized = value.slice(2); // remove 88 prefix for further checks
  } else if (value.length !== 11) {
    return Promise.reject(new Error("Mobile banking number must be 11 digits"));
  }

  // Step 3: must start with valid BD mobile operator code
  const validOperators = /^01[3-9]/;
  if (!validOperators.test(normalized)) {
    return Promise.reject(new Error("Please enter a valid Bangladeshi number"));
  }

  return Promise.resolve();
};

// ✅ Bangladeshi Bank Account Number Validator
export const validateBDBankAccount = async (_: any, value: string) => {
  if (!value) {
    return Promise.resolve();
  }

  // Step 1: only digits
  const digitsOnlyRegex = /^\d+$/;
  if (!digitsOnlyRegex.test(value)) {
    return Promise.reject(new Error("Account number must contain only digits"));
  }

  // Step 2: length between 10 and 17
  if (value.length < 10 || value.length > 17) {
    return Promise.reject(
      new Error("Account number must be between 10 and 17 digits"),
    );
  }

  return Promise.resolve();
};

// ✅ Bangladeshi Bank Account Number Validator
export const validateBDRoutingNumber = async (_: any, value: string) => {
  if (!value) {
    return Promise.resolve();
  }

  // Step 1: only digits
  const digitsOnlyRegex = /^\d+$/;
  if (!digitsOnlyRegex.test(value)) {
    return Promise.reject(new Error("Routing number must contain only digits"));
  }

  // Step 2: length between 10 and 17
  if (value.length < 9 || value.length > 9) {
    return Promise.reject(new Error("Routing number must be 9 digits"));
  }

  return Promise.resolve();
};

// ✅ Swift Code (BIC) Validator
export const validateSwiftCode = async (_: any, value: string) => {
  if (!value) {
    return Promise.resolve();
  }

  // Step 1: only uppercase letters and numbers
  const swiftRegex = /^[A-Z0-9]+$/;
  if (!swiftRegex.test(value)) {
    return Promise.reject(
      new Error("SWIFT code must contain only uppercase letters and digits"),
    );
  }

  // Step 2: length between 8 and 11 characters
  if (value.length < 8 || value.length > 11) {
    return Promise.reject(
      new Error("SWIFT code must be between 8 and 11 characters long"),
    );
  }

  return Promise.resolve();
};

// ✅ Reference ID Validator
export const validateReferenceId = async (_: any, value: string) => {
  if (!value) {
    return Promise.resolve();
  }

  // Step 1: only uppercase letters (A–Z) and digits (0–9)
  const refIdRegex = /^[A-Z0-9]+$/;
  if (!refIdRegex.test(value)) {
    return Promise.reject(
      new Error(
        "Reference ID must contain only uppercase letters (A–Z) and digits (0–9)",
      ),
    );
  }

  // Step 2: length between 8 and 20
  if (value.length < 8 || value.length > 20) {
    return Promise.reject(
      new Error("Reference ID must be between 8 and 20 characters long"),
    );
  }

  return Promise.resolve();
};

// ✅ Bangladeshi Bank Account Number Validator
export const validateBankAccountName = async (_: any, value: string) => {
  if (!value) {
    return Promise.resolve();
  }

  // Step 1: check length
  if (value.length < 2 || value.length > 50) {
    return Promise.reject(new Error("Name must be 2-50 characters"));
  }

  // Step 2: check for numbers
  const hasNumber = /\d/;
  if (hasNumber.test(value)) {
    return Promise.reject(new Error("Numbers not allowed"));
  }

  // Step 3: check for invalid special characters
  // Allow only letters, spaces, dot, hyphen
  const invalidChar = /[^a-zA-Z\s.-]/;
  if (invalidChar.test(value)) {
    return Promise.reject(new Error("Special characters not allowed"));
  }

  return Promise.resolve();
};

// ✅ Positive Number Validator
export const validatePositiveNumber = async (_: any, value: string) => {
  if (!Number(value)) {
    return Promise.resolve();
  }
  
  // Step 1: allow digits with optional decimal part
  const numberRegex = /^\d+(\.\d+)?$/;
  if (!numberRegex.test(value)) {
    return Promise.reject(new Error("Number must be a valid positive number"));
  }

  // Convert to number for further checks
  const numericValue = Number(value);

  // Step 2: check for zero
  if (numericValue === 0) {
    return Promise.reject(new Error("Number must be greater than zero"));
  }

  // Step 3: check for negative
  if (numericValue < 0) {
    return Promise.reject(new Error("Number must be positive"));
  }

  return Promise.resolve();
};
